import { useEffect, useRef } from 'react';
import { useAppDispatch } from '../store';
import {
  selectIsAuthenticated,
  selectIsAuthInitialized,
} from '../store/slices/authSlice';
import { useLogout } from './useLogout';
import { useAuthContext } from '@coffeeplexity/shared';
import { logger } from '../utils/logger';

/**
 * Custom hook that automatically logs out users when they become unauthenticated
 * This handles both Redux auth state and shared AuthContext
 */
export const useAutoLogout = () => {
  const dispatch = useAppDispatch();
  const { logout: contextLogout, isAuthenticated: contextAuthenticated } =
    useAuthContext();
  const { logout: reduxLogout } = useLogout();

  // Track previous authentication state to detect changes
  const prevAuthState = useRef<boolean | null>(null);
  const hasLoggedOut = useRef(false);

  useEffect(() => {
    // Only proceed if we have a previous state to compare against
    if (prevAuthState.current !== null) {
      const wasAuthenticated = prevAuthState.current;
      const isNowAuthenticated = contextAuthenticated;

      // If user was authenticated but is now unauthenticated, trigger logout
      if (wasAuthenticated && !isNowAuthenticated && !hasLoggedOut.current) {
        logger.log('🔄 Auto-logout triggered: User became unauthenticated');
        hasLoggedOut.current = true;

        // Use the context logout which handles both systems
        contextLogout().finally(() => {
          hasLoggedOut.current = false;
        });
      }
    }

    // Update the previous state
    prevAuthState.current = contextAuthenticated;
  }, [contextAuthenticated, contextLogout]);

  // Reset logout flag when user becomes authenticated again
  useEffect(() => {
    if (contextAuthenticated) {
      hasLoggedOut.current = false;
    }
  }, [contextAuthenticated]);
};
